import { Injectable } from '@angular/core';
import { PreloadingStrategy, Route } from '@angular/router';
import { Observable, of, timer, forkJoin, EMPTY, lastValueFrom } from 'rxjs';
import { switchMap, catchError } from 'rxjs/operators';

interface PreloadConfig {
  enabled: boolean;
  delay: number;
  priority: 'high' | 'medium' | 'low';
  conditions?: string[];
}

interface PreloadItem {
  route: Route;
  load: () => Observable<unknown>;
  priority: number;
  config: PreloadConfig;
}

/**
 * This class is used to preload routes selectively based on the priority and delay.
 */

@Injectable({
  providedIn: 'root',
})
export class SelectivePreloadingStrategy implements PreloadingStrategy {
  private preloadedModules: string[] = [];

  // Group queues by priority for parallel loading
  private preloadQueues: {
    high: PreloadItem[];
    medium: PreloadItem[];
    low: PreloadItem[];
  } = {
    high: [],
    medium: [],
    low: [],
  };

  private isPreloading = {
    high: false,
    medium: false,
    low: false,
  };

  private readonly MAX_CONCURRENT_LOADS = 3;

  /**
   * Preloads a route based on the priority and delay.
   * @param route - The route to preload.
   * @param load - The function to load the route.
   * @returns An observable of the loaded route.
   */
  preload(route: Route, load: () => Observable<unknown>): Observable<unknown> {
    const config = this.getPreloadConfig(route);

    if (!config.enabled) {
      return of(undefined);
    }

    const priority = this.getPriorityWeight(config.priority);
    const priorityName = config.priority;

    // Add to appropriate priority queue
    this.preloadQueues[priorityName].push({
      route,
      load,
      priority,
      config,
    });

    // Start processing this priority queue if not already running
    if (!this.isPreloading[priorityName]) {
      this.processPreloadQueue(priorityName);
    }

    return of(undefined);
  }

  /**
   * Processes a preload queue based on the priority.
   * @param priorityName - The priority of the queue to process.
   * @returns A promise of the processed queue.
   */
  private async processPreloadQueue(
    priorityName: 'high' | 'medium' | 'low'
  ): Promise<void> {
    const queue = this.preloadQueues[priorityName];

    if (queue.length === 0) {
      this.isPreloading[priorityName] = false;
      return;
    }

    this.isPreloading[priorityName] = true;

    // Process items in batches for parallel loading
    while (queue.length > 0) {
      const batch = queue.splice(0, this.MAX_CONCURRENT_LOADS);

      // Load all items in this batch in parallel
      const batchLoads = batch.map(item =>
        this.loadRouteWithDelay(item).pipe(
          catchError(error => {
            console.warn(`Failed to preload route: ${item.route.path}`, error);
            return of(undefined);
          })
        )
      );

      try {
        // Wait for all items in batch to complete before processing next batch
        await lastValueFrom(forkJoin(batchLoads));

        // Small delay between batches to prevent overwhelming the browser
        if (queue.length > 0) {
          await lastValueFrom(timer(100));
        }
      } catch (error) {
        console.warn(
          `Batch loading failed for priority ${priorityName}:`,
          error
        );
      }
    }

    this.isPreloading[priorityName] = false;

    // After finishing high priority, start medium priority
    // After medium priority, start low priority
    this.startNextPriorityQueue(priorityName);
  }

  /**
   * Loads a route with a delay based on the priority.
   * @param item - The item to load.
   * @returns An observable of the loaded route.
   */
  private loadRouteWithDelay(item: PreloadItem): Observable<unknown> {
    const { route, load, config } = item;

    return timer(config.delay).pipe(
      switchMap(() => {
        this.preloadedModules.push(route.path || 'unknown');
        return load();
      }),
      catchError(error => {
        console.warn(`Failed to preload route: ${route.path}`, error);
        return EMPTY;
      })
    );
  }

  /**
   * Starts the next priority queue based on the completed priority.
   * @param completedPriority - The priority of the completed queue.
   */
  private startNextPriorityQueue(
    completedPriority: 'high' | 'medium' | 'low'
  ): void {
    // Start next priority queue if current one is finished
    if (
      completedPriority === 'high' &&
      !this.isPreloading.medium &&
      this.preloadQueues.medium.length > 0
    ) {
      this.processPreloadQueue('medium');
    } else if (
      completedPriority === 'medium' &&
      !this.isPreloading.low &&
      this.preloadQueues.low.length > 0
    ) {
      this.processPreloadQueue('low');
    }
  }

  /**
   * Gets the preload config for a route.
   * @param route - The route to get the preload config for.
   * @returns The preload config for the route.
   */
  private getPreloadConfig(route: Route): PreloadConfig {
    // Check route-specific preload data
    if (route.data?.['preload'] === false) {
      return { enabled: false, delay: 0, priority: 'low' };
    }
    // Default: preload with medium priority if explicitly marked
    return {
      enabled: route.data?.['preload'] === true,
      delay: route.data?.['delay'] || 500, // Allow custom delays
      priority: route.data?.['priority'] || 'medium',
    };
  }

  /**
   * Gets the priority weight for a priority.
   * @param priority - The priority to get the weight for.
   * @returns The weight for the priority.
   */
  private getPriorityWeight(priority: 'high' | 'medium' | 'low'): number {
    switch (priority) {
      case 'high':
        return 3;
      case 'medium':
        return 2;
      case 'low':
        return 1;
      default:
        return 1;
    }
  }

  /**
   * Gets the preload status.
   * @returns The preload status.
   */
  getPreloadStatus() {
    return {
      preloadedModules: this.preloadedModules,
      queueSizes: {
        high: this.preloadQueues.high.length,
        medium: this.preloadQueues.medium.length,
        low: this.preloadQueues.low.length,
      },
      isPreloading: this.isPreloading,
    };
  }
}
